import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';

import { config } from '../../config';
import { Guid } from '../../models/guid.model';
import { MemberQuery } from '../../../account/member/member.query';

import { Observable } from 'rxjs';
import { share } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    private jwtService: JwtHelperService,
    private memberQuery: MemberQuery
  ) {}

  cardId: number;

  public getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    let refreshToken = localStorage.getItem('refresh');
    let sessionStorageSessionId = sessionStorage.getItem('SessionId');
    if (refreshToken) {
      const decodedRefreshToken = this.jwtService.decodeToken(refreshToken);
      if (decodedRefreshToken) {
        let sessionId = decodedRefreshToken.login_id;
        if (sessionId) {
          headers = headers.append('SessionId', sessionId);
        }
      }
    } else if (sessionStorageSessionId !== null) {
      headers = headers.append('SessionId', sessionStorageSessionId);
    } else {
      let sessionId = new Guid().toString();
      sessionStorage.setItem('SessionId', sessionId);
      headers = headers.append('SessionId', sessionId);
    }

    let token = localStorage.getItem('token');
    if (token) {
      headers = headers.append('Authorization', `Bearer ${token}`);
    }

    let lcid = localStorage.getItem('lcid');
    if (lcid === null) {
      try {
        lcid = this.memberQuery.getUserContext().lcid;
        localStorage.setItem('lcid', lcid);
      } catch (error) {
        lcid = config.defaultLcid;
      }
    }

    headers = headers.append('X-Fortress-LCID', lcid);

    return headers;
  }

  /** Gets an observable of type specified from baseUrl/entity/childRoute */
  public getChildData<T>(entity: string, childRoute: string): Observable<T> {
    return this.http.get<T>(`${config.baseApiUrl}/api/${entity}/${childRoute}`, { headers: this.getHeaders() }).pipe(share());
  }

  /** Returns an observable array of type specified from baseUrl/entity */
  public get<T>(entity: string): Observable<T[]> {
    return this.http.get<T[]>(`${config.baseApiUrl}/api/${entity}`, { headers: this.getHeaders() }).pipe(share());
  }

  /** Returns an observable of type specified from baseUrl/entity */
  public getAnySingle<T>(entity: string): Observable<T> {
    return this.http.get<T>(`${config.baseApiUrl}/api/${entity}`, { headers: this.getHeaders() }).pipe(share());
  }

  /** Returns an observable with full HTTP response including headers from baseUrl/entity */
  public getAnySingleWithHeaders<T>(entity: string, customHeaders: HttpHeaders): Observable<any> {
    return this.http.get<T>(`${config.baseApiUrl}/api/${entity}`, { 
      headers: customHeaders, 
      observe: 'response' 
    }).pipe(share());
  }


  /** Returns an observable array of type specified from baseUrl/entity */
  public getForParent<T>(parentEntity: string, parentId: number, childEntity: string): Observable<T[]> {
    return this.http
      .get<T[]>(`${config.baseApiUrl}/api/${parentEntity}/${parentId}/${childEntity}`, {
        headers: this.getHeaders(),
      })
      .pipe(share());
  }

  /** Returns an observable of type specified from baseUrl/entity/id */
  public getById<T>(entity: string, id: string, queryParams?: HttpParams): Observable<T> {
    return this.http
      .get<T>(`${config.baseApiUrl}/api/${entity}/${id}`, { headers: this.getHeaders(), params: queryParams })
      .pipe(share());
  }

  /** Posts the given item to baseUrl/entity and returns an observable of the same type */
  public post<T>(entity: string, item: T, queryParams?: HttpParams): Observable<any> {
    return this.http.post<T>(`${config.baseApiUrl}/api/${entity}`, item, { headers: this.getHeaders(), params: queryParams });
  }

  /** Posts data with custom headers */
  public postWithCustomHeaders<T>(entity: string, item: T, customHeaders: HttpHeaders): Observable<any> {
    return this.http.post<T>(`${config.baseApiUrl}/api/${entity}`, item, { headers: customHeaders });
  }

  /** Posts the given item array to baseUrl/entity and returns an observable of the same type */
  public postWithArray<T>(entity: string, item: T[]): Observable<any> {
    return this.http.post<T>(`${config.baseApiUrl}/api/${entity}`, item, { headers: this.getHeaders() });
  }

  /** Posts the given item to baseUrl/entity and returns an observable of the same type */
  public postForParent<T>(parentEntity: string, parentId: number, childEntity: string, item: T): Observable<any> {
    return this.http.post<T>(`${config.baseApiUrl}/api/${parentEntity}/${parentId}/${childEntity}`, item, {
      headers: this.getHeaders(),
    });
  }

  /** Puts the given item to baseUrl/entity/id and returns an observable of the same type */
  public put<T>(entity: string, id: any, item: T): Observable<any> {
    return this.http.put<T>(`${config.baseApiUrl}/api/${entity}/${id}`, item, { headers: this.getHeaders() });
  }

  /** Sends a delete request to baseUrl/entity/id */
  public delete(entity: string, id: any) {
    return this.http.delete(`${config.baseApiUrl}/api/${entity}/${id}`, { headers: this.getHeaders() });
  }

  public setCardId(id: number) {
    this.cardId = id;
  }

  public getFile(entity: string) {
    let headers = this.getHeaders();

    headers = headers.delete('Content-Type');

    return this.http.get(`${config.baseApiUrl}/api/${entity}`, { headers: headers, responseType: 'blob' }).pipe(share());
  }

  public uploadForm(entity: string, formData: FormData) {
    let headers = this.getHeaders();

    headers = headers.delete('Content-Type');

    return this.http.post(`${config.baseApiUrl}/api/${entity}`, formData, { headers: headers, observe: 'events' as const });
  }

  public uploadFacialForm(entity: string, formData: FormData, accessKey?: string) {
    let headers = this.getHeaders();
    headers = headers.delete('Content-Type');
    if (accessKey) {
      headers = headers.append('X-Fortress-FacialAccessKey', accessKey);
    }

    return this.http.post(`${config.baseApiUrl}/api/${entity}`, formData, { headers: headers, observe: 'response' as const });
  }

  public validateFacialKey<T>(entity: string, accessKey: string): Observable<T> {
    let headers = this.getHeaders();
    headers = headers.delete('Content-Type');
    headers = headers.append('X-Fortress-FacialAccessKey', accessKey);

    return this.http.get<T>(`${config.baseApiUrl}/api/${entity}`, { headers: headers }).pipe(share());
  }
}
